/**
 * End Customer Dashboard API service
 *
 * Provides data for the End Customer dashboard including:
 * - VM stats and counts
 * - Resource allocation
 * - Billing history
 * - Alerts and activities
 */

import { apiClient } from './api'

// --- Types ---

export type DashboardStats = {
  total_vms: number
  active_vms: number
  stopped_vms: number
  creating_vms: number
  error_vms: number
  cpu_allocated: number
  ram_allocated: number
  storage_used: number
  storage_total: number
  monthly_cost: number
  active_alerts: number
}

export type ResourceUsage = {
  cpu_total_allocated: number
  ram_total_allocated: number
  storage_total_allocated: number
  active_vms: number
  total_vms: number
}

export type BillingHistoryPoint = {
  month: string
  month_label: string
  amount: number
}

export type DashboardAlert = {
  id: string
  type: 'error' | 'warning' | 'info'
  severity: 'high' | 'medium' | 'low'
  title: string
  description: string
  vm_name: string
  timestamp: string
}

export type RecentActivity = {
  id: string
  type: string
  title: string
  description: string
  timestamp: string
  level: string
}

export type VMResource = {
  allocated: number
  usage: number
}

export type DashboardVM = {
  id: string
  name: string
  status: string
  cpu: VMResource
  memory: VMResource
  storage: VMResource
  uptime: string | null
  ip: string | null
  os: string
  location: string
  created_at: string | null
}

export type CustomerDashboardResponse = {
  stats: DashboardStats
  resource_usage: ResourceUsage
  billing_history: BillingHistoryPoint[]
  alerts: DashboardAlert[]
  recent_activities: RecentActivity[]
  vms: DashboardVM[]
  timestamp: string
}

// --- API Functions ---

/**
 * Get complete dashboard data for end customer
 */
export const getCustomerDashboard = async (): Promise<CustomerDashboardResponse> => {
  return apiClient.get('/api/v1/end-customer/dashboard')
}

// --- Helper Functions ---

/**
 * Get color classes for VM status
 */
export const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    running: 'bg-green-100 text-green-800',
    stopped: 'bg-red-100 text-red-800',
    creating: 'bg-yellow-100 text-yellow-800',
    starting: 'bg-yellow-100 text-yellow-800',
    stopping: 'bg-orange-100 text-orange-800',
    error: 'bg-red-100 text-red-800',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

/**
 * Get label for VM status in Portuguese
 */
export const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    running: 'Online',
    stopped: 'Parada',
    creating: 'Criando',
    starting: 'Iniciando',
    stopping: 'Parando',
    error: 'Erro',
    terminated: 'Terminada',
  }
  return labels[status] || status
}

/**
 * Get icon for alert severity
 */
export const getAlertSeverityColor = (severity: string): string => {
  const colors: Record<string, string> = {
    high: 'text-red-500',
    medium: 'text-yellow-500',
    low: 'text-blue-500',
  }
  return colors[severity] || 'text-gray-500'
}

/**
 * Get activity type icon name
 */
export const getActivityTypeIcon = (type: string): string => {
  const icons: Record<string, string> = {
    vm_created: 'Plus',
    vm_started: 'Play',
    vm_stopped: 'Square',
    vm_restarted: 'RotateCcw',
    vm_action: 'Zap',
    login: 'LogIn',
    backup_completed: 'Shield',
    alert_triggered: 'AlertCircle',
    payment_processed: 'DollarSign',
    info: 'Info',
  }
  return icons[type] || 'Activity'
}

/**
 * Get activity type color
 */
export const getActivityTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    vm_created: 'text-green-600',
    vm_started: 'text-green-600',
    vm_stopped: 'text-red-600',
    vm_restarted: 'text-purple-600',
    vm_action: 'text-blue-600',
    login: 'text-gray-600',
    backup_completed: 'text-blue-600',
    alert_triggered: 'text-yellow-600',
    payment_processed: 'text-emerald-600',
    info: 'text-gray-600',
  }
  return colors[type] || 'text-gray-600'
}

/**
 * Format currency value
 */
export const formatCurrency = (value: number, currency: string = 'BRL'): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(value)
}

/**
 * Format bytes/storage value
 */
export const formatStorage = (gb: number): string => {
  if (gb >= 1000) {
    return `${(gb / 1000).toFixed(1)} TB`
  }
  return `${gb} GB`
}

/**
 * Format relative time
 */
export const formatRelativeTime = (timestamp: string): string => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMins < 1) return 'Agora mesmo'
  if (diffMins < 60) return `${diffMins} min atrás`
  if (diffHours < 24) return `${diffHours}h atrás`
  if (diffDays < 7) return `${diffDays} dias atrás`

  return date.toLocaleDateString('pt-BR')
}

// --- Export ---

const endCustomerDashboardApi = {
  getCustomerDashboard,
  getStatusColor,
  getStatusLabel,
  getAlertSeverityColor,
  getActivityTypeIcon,
  getActivityTypeColor,
  formatCurrency,
  formatStorage,
  formatRelativeTime,
}

export default endCustomerDashboardApi
